// React
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Utils
import { COMPAIGNS_ACTION_TYPE } from '~/utils/constants';

// MUI Components
import { Box, Tooltip, Typography, Paper, Chip, Skeleton } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';

// Styles
import './styles.scss';

const DATE_FORMAT_OPTIONS = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
const DEFAULT_HEADER_HEIGHT = 48;
const DEFAULT_ROW_HEIGHT = 56;
const BATCH_ROW_VERTICAL_PADDING = 32;
const BATCH_DATE_LINE_HEIGHT = 21;
const GRID_BORDER_ALLOWANCE = 2;
const SCROLL_ACTIVATION_TOLERANCE = 8;

// Converts API date string "Thursday May 07, 2026" → "Thu, May 7, 2026" to match single mailer format.
const formatBatchDate = (dateStr) => {
  if (!dateStr) return dateStr;
  try {
    // Strip the leading weekday ("Thursday ") so Date() can parse "May 07, 2026" reliably.
    const withoutWeekday = dateStr.replace(/^[A-Za-z]+\s/, '');
    const d = new Date(withoutWeekday);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
  } catch {
    return dateStr;
  }
};

const CampaignsMailDateTable = (props) => {
  const {
    mailedDateBreakdown,
    selectedDate,
    fallbackTemplates = [],
    fallbackMailedDate = '',
  } = props;

  const tableFrameRef = useRef(null);
  const [scrollFrameHeight, setScrollFrameHeight] = useState(null);

  const rows = useMemo(() => {
    if (!selectedDate) return [];

    const hasBreakdown = Array.isArray(mailedDateBreakdown) && mailedDateBreakdown.length > 0;
    const hasFallback =
      Array.isArray(fallbackTemplates) && fallbackTemplates.length > 0 && !!fallbackMailedDate;

    if (hasBreakdown) {
      const hasBatchGroupKey = mailedDateBreakdown[0]?.batchGroupKey !== undefined;

      if (hasBatchGroupKey) {
        const groupMap = {};
        const groupOrder = [];

        mailedDateBreakdown.forEach((entry) => {
          const key = entry.batchGroupKey;
          if (!groupMap[key]) {
            groupMap[key] = {
              templateId: entry.templateId,
              templateName: entry.templateName,
              product: entry.product,
              batches: [],
            };
            groupOrder.push(key);
          }
          groupMap[key].batches.push({
            batchNumber: entry.batchNumber,
            mailedDate: entry.mailedDate,
          });
        });

        const computedRows = groupOrder.map((key, index) => {
          const group = groupMap[key];
          return {
            id: index,
            templateName: `ID:${group.templateId} ${group.templateName}`,
            productType: group.product?.productType,
            productId: group.product?.id,
            // Sort batches by batchNumber so they display in order
            batches: group.batches.sort((a, b) => a.batchNumber - b.batchNumber),
          };
        });

        return computedRows;
      }

      // Legacy format: one entry per automation step, no batching
      const computedRows = mailedDateBreakdown.map(
        ({ templateId, templateName, mailedDate, product }, index) => ({
          id: index,
          templateName: `ID:${templateId} ${templateName}`,
          mailDate: mailedDate,
          productType: product?.productType,
          productId: product?.id,
        })
      );
      return computedRows;
    }

    if (hasFallback) {
      return fallbackTemplates.map((tpl, index) => ({
        id: index,
        templateName: ` ${tpl?.label || tpl?.templateName || ''}`.trim(),
        mailDate: fallbackMailedDate,
        productType: tpl?.productType,
        productId: tpl?.productId,
      }));
    }

    return [];
  }, [fallbackMailedDate, fallbackTemplates, mailedDateBreakdown, selectedDate]);

  const loading = Boolean(selectedDate && rows.length === 0);
  const hasBatches = rows.some((row) => row.batches?.length > 0);
  const gridContentHeight = useMemo(() => {
    const rowsHeight = rows.reduce((height, row) => {
      const batchCount = row.batches?.length || 0;
      const rowHeight =
        batchCount > 1
          ? Math.max(
              DEFAULT_ROW_HEIGHT,
              BATCH_ROW_VERTICAL_PADDING + batchCount * BATCH_DATE_LINE_HEIGHT
            )
          : DEFAULT_ROW_HEIGHT;

      return height + rowHeight;
    }, 0);

    return DEFAULT_HEADER_HEIGHT + rowsHeight + GRID_BORDER_ALLOWANCE;
  }, [rows]);
  const getCompactMailDateValue = useCallback((row) => {
    if (row?.batches?.length > 0) return 'Batch dates';
    return row?.mailDate ? 'Mail date' : '-';
  }, []);
  const getCompactTemplateValue = useCallback((row) => {
    return row?.templateName ? 'Template' : '-';
  }, []);
  const getCompactProductValue = useCallback((row) => {
    return row?.productType ? 'Product' : '-';
  }, []);

  const columns = [
    {
      field: 'templateName',
      headerName: 'Template',
      flex: 1.2,
      minWidth: 180,
      compactShrinkMinWidth: 140,
      compactShrinkPriority: 0,
      compactValueGetter: getCompactTemplateValue,
      headerAlign: 'left',
      align: 'left',
      sortable: false,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.templateName} placement="top-start">
            <Typography className="templateNameCell">{params.row.templateName}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'productType',
      headerName: 'Product',
      flex: 1.2,
      minWidth: 180,
      compactShrinkMinWidth: 90,
      compactShrinkPriority: 0,
      compactValueGetter: getCompactProductValue,
      headerAlign: 'left',
      align: 'left',
      sortable: false,
      renderCell: (params) => {
        return (
          <Tooltip
            title={
              COMPAIGNS_ACTION_TYPE.find(
                (product) => +product?.productId === +params?.row?.productId
              )?.label || params?.row?.productType
            }
            placement="top-start"
          >
            <Typography className="templateNameCell">
              {COMPAIGNS_ACTION_TYPE.find(
                (product) => +product?.productId === +params?.row?.productId
              )?.label || params?.row?.productType}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'mailDate',
      headerName: 'Mail Date',
      flex: 1.4,
      minWidth: 250,
      compactMaxWidth: 250,
      compactShrinkMinWidth: 145,
      compactShrinkPriority: 1,
      compactValueGetter: getCompactMailDateValue,
      headerAlign: 'left',
      align: 'left',
      sortable: false,
      renderCell: (params) => {
        // New format: batch dates come directly from the API — no frontend calculation.
        if (params.row.batches?.length > 0) {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                width: '100%',
                alignItems: 'flex-start',
              }}
            >
              {params.row.batches.map(({ batchNumber, mailedDate }) => (
                <Typography key={batchNumber} className="dateText" sx={{ lineHeight: '1.5' }}>
                  Batch {batchNumber} - {formatBatchDate(mailedDate)}
                </Typography>
              ))}
            </Box>
          );
        }
        return <Typography className="dateText">{params.row.mailDate}</Typography>;
      },
    },
  ];

  const LoadingSkeleton = () => (
    <Box className="skeletonContainer">
      {[1, 2, 3].map((item) => (
        <Box key={item} className="skeletonRow">
          <Skeleton variant="text" width="30%" height={24} />
          <Skeleton variant="text" width="30%" height={24} />
          <Skeleton variant="rectangular" width={80} height={20} />
        </Box>
      ))}
    </Box>
  );

  useLayoutEffect(() => {
    if (!selectedDate || rows.length === 0) {
      setScrollFrameHeight(null);
      return undefined;
    }

    let frameId;
    let resizeObserver;

    const measureTable = () => {
      const frame = tableFrameRef.current;
      const tableColumn = frame?.closest('.orderDetailsTableColumn');

      if (!frame || !tableColumn) {
        setScrollFrameHeight(null);
        return;
      }

      const availableHeight = tableColumn.clientHeight;
      const nextScrollHeight =
        availableHeight > 0 && gridContentHeight > availableHeight + SCROLL_ACTIVATION_TOLERANCE
          ? availableHeight
          : null;

      setScrollFrameHeight((currentHeight) =>
        currentHeight === nextScrollHeight ? currentHeight : nextScrollHeight
      );
    };

    const scheduleMeasure = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = undefined;
        measureTable();
      });
    };

    measureTable();
    scheduleMeasure();

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMeasure);

      if (tableFrameRef.current) {
        resizeObserver.observe(tableFrameRef.current);
        const tableColumn = tableFrameRef.current.closest('.orderDetailsTableColumn');
        if (tableColumn) resizeObserver.observe(tableColumn);
      }
    } else {
      window.addEventListener('resize', scheduleMeasure);
    }

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [rows.length, selectedDate, hasBatches, gridContentHeight]);

  if (loading) {
    return (
      <Box className="campaignsMailDateContainer campaignsMailDateContainer--campaign">
        <Paper
          className="tableContainer"
          ref={tableFrameRef}
          sx={{
            marginBottom: { xs: '20px', sm: '24px' },
          }}
        >
          <LoadingSkeleton />
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="campaignsMailDateContainer campaignsMailDateContainer--campaign">
      {selectedDate && (
        <Paper
          className="tableContainer"
          ref={tableFrameRef}
          sx={{
            marginBottom: { xs: '20px', sm: '24px' },
          }}
        >
          {rows.length !== 0 && (
            <Box className="dataGridWrapper">
              <DataGrid
                rows={rows}
                columns={columns}
                autoHeight={!scrollFrameHeight}
                compactColumns={false}
                measureContentWidth={false}
                preserveColumnLayout
                getRowId={(row) => row?.id}
                hideFooter
                hideFooterPagination
                hideFooterSelectedRowCount
                disableColumnMenu
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                disableRowSelectionOnClick
                rowHeight={DEFAULT_ROW_HEIGHT}
                className={`mailDateTable ${
                  scrollFrameHeight ? 'mailDateTable--scrollFrame' : ''
                }`.trim()}
                getRowHeight={() => (hasBatches ? 'auto' : DEFAULT_ROW_HEIGHT)}
                sx={{
                  ...(scrollFrameHeight ? { height: `${scrollFrameHeight}px` } : {}),
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-main': {
                    borderRadius: 'inherit',
                    overflow: 'hidden',
                  },
                  '& .MuiDataGrid-cell': {
                    alignItems: 'flex-start',
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CampaignsMailDateTable;
